import useDelayedQuery from '@hooks/useDelayedQuery.js';
import useTwitchRequest from '@hooks/useTwitchRequest.js';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { useQueryClient } from '@tanstack/react-query';

const isDev =
  import.meta.env.VITE_APP_ENV === 'dev' ||
  import.meta.env.VITE_APP_ENV === 'development';

const fakeTwitchCharityCampaign = {
  id: '2H0G1LuHTeaSjBi9bF5xTgwPPlC',
  broadcaster_id: '58338138',
  broadcaster_name: 'Warchant',
  broadcaster_login: 'warchant',
  charity_name: "St. Jude Children's Research Hospital",
  charity_description:
    "St. Jude Children's Research Hospital is leading the way the world understands, treats and defeats childhood cancer and other life-threatening diseases.  Our mission is clear: Finding cures. Saving children. Families never receive a bill from St. Jude for treatment, travel, housing or food - because all family should worry about is helping their child live. Please help St. Jude continue its mission by donating today.",
  charity_logo:
    'https://pics.paypal.com/00/s/MWFkZTBlZmMtOGZiNS00MmY2LTk1N2QtYjRiODkzODIxNjQw/file.JPG',
  charity_website: 'stjude.org',
  current_amount: {
    value: 3500,
    currency: 'USD',
    decimal_places: 2,
  },
  target_amount: {
    value: 10000,
    currency: 'USD',
    decimal_places: 2,
  },
};

const twitchLambdaURI = import.meta.env.VITE_TWITCH_URI;

export const useTwitchCampaigns = (config, disabled) => {
  const request = useTwitchRequest({
    twitchAccessToken: config.twitchAccessToken,
    tokenValidated: !disabled,
  });

  const twitchUsername = config.twitchUsername;
  const disabledRef = useRef(disabled);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!disabled && disabledRef.current) {
      disabledRef.current = false;
      queryClient
        .invalidateQueries({
          queryKey: ['twitch', twitchUsername, 'getCampaigns'],
        })
        .then();
    } else if (disabled && !disabledRef.current) {
      disabledRef.current = true;
    }
  }, [disabled, queryClient, twitchUsername]);

  const { data: campaigns, isFetching: campaignsLoading } = useDelayedQuery({
    queryKey: ['twitch', twitchUsername, 'getCampaigns'],
    placeholderData: undefined,

    queryFn: async () => {
      if (!config.twitchBroadcasterID || config.twitchBroadcasterID === 'null')
        return [];

      const headers = {
        Authorization: `Bearer ${config.twitchAccessToken}`,
      };

      return fetch(`${twitchLambdaURI}/getUserCampaigns`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ broadcasterID: config.twitchBroadcasterID }),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result)
            return result.filter((campaign) =>
              campaign['charity_name'].includes('St. Jude')
            );
          return [];
        })
        .then((data) => {
          if (data.length === 0 && isDev) {
            return [
              {
                ...fakeTwitchCharityCampaign,
                broadcaster_id: config.twitchBroadcasterID,
              },
            ];
          }

          return data;
        });
    },
  });

  const deleteCampaign = useCallback(() => {
    try {
      // UDP: `/campaigns/twitch/${campaignID}`
      return request('/deleteUserCampaign', { twitchUsername }, 'POST').then(
        (result) => {
          if (result && result.success) return result.data;
          return [];
        }
      );
    } catch (err) {
      throw new Error(err.message);
    }
  }, [request, twitchUsername]);

  const writeCampaign = useCallback(
    async (campaign) => {
      const headers = {
        Authorization: `Bearer ${config.twitchAccessToken}`,
      };

      const response = await fetch(
        `${twitchLambdaURI}/writeUserCharityCampaign`,
        {
          method: 'POST',
          body: JSON.stringify(campaign),
          headers,
        }
      );

      const result = await response.json();
      if (result) return result;
      return null;
    },
    [config.twitchAccessToken]
  );

  return { campaigns, campaignsLoading, writeCampaign, deleteCampaign };
};
